import * as React from 'react';
import { useSelector } from 'react-redux';
import { Button, Box, MuiBox, FlexBox, Grid, InputField, Typography, color } from '@kis-technologies/kis-ui';
import { selectors as authSelectors, actions as authActions } from 'store/auth';
import { KioskControlCenter } from 'components/KioskControlCenter';
import { validateEmail } from 'utils/validation';
import { useActions } from 'hooks/useActions';
import { useStyles } from './styled';

const INVALID_EMAIL = 'Invalid email format';

const ForgotPasswordView: React.FC = () => {
  // ACTION CREATORS AND SELECTORS

  const { requestResetPassword } = useActions({ requestResetPassword: authActions.requestResetPassword });
  const error = useSelector(authSelectors.selectActionError);
  const updating = useSelector(authSelectors.selectActionPending);
  const resetSuccess = useSelector(authSelectors.selectActionSuccessful);
  const classes = useStyles();

  // EMAIL INPUT STATES
  const [email, setEmail] = React.useState('');

  // INVALID MESSAGE WHEN EMAIL INCORRECT FORMAT
  const [invalidMsg, setInvalidMsg] = React.useState<string | null>(null);

  // ON UPDATE OF ERROR - SET THE ERROR AS INVALID MESSAGE
  React.useEffect(() => {
    if (!updating) {
      setInvalidMsg(error);
    }
  }, [error, updating]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // EMAIL VALIDATION
  const emailValidationFn = async (value: string | number | undefined) => {
    // VALIDATION SCHEMA
    const schema = validateEmail(INVALID_EMAIL);

    // VALIDATE FN
    const result = await schema.validate(value).catch((err) => err);

    // IF THERE WAS ERROR MESSAGE - SET IT TO INVALID MESSAGE
    setInvalidMsg(result && result.message);

    if (result && !result.message) {
      // FIRES UPDATE ADMIN ASYNC FN
      requestResetPassword(email);
    }
  };

  // INPUT CHANGE
  const onInputChange = (setFn: React.Dispatch<React.SetStateAction<string>>, value: string) => {
    // IF INVALID MESSAGE EXISTS SET IT TO NULL
    if (invalidMsg) setInvalidMsg(null);

    // FIRES THE APPROPRIATE FN: SETPASSWORD | SETCONFIRMPASSWORD WITH VALUE
    setFn(value);
  };

  // FORM SUBMIT
  const onSaveClick = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    e.preventDefault();
    emailValidationFn(email);
  };

  const buttonDisabled = !email.length || !!invalidMsg;
  return (
    <Grid container direction="row" className={classes.forgotPasswordOuterContainer}>
      {/* LEFT OF CARD */}
      <Grid item xl={6} md={6} lg={6} sm={12} xs={12} className={classes.forgotPasswordImageBlock}>
        <FlexBox fullHeight bg="light-gray" sx={{ minHeight: '100%' }}>
          <KioskControlCenter />
        </FlexBox>
      </Grid>
      {/* RIGHT OF CARD - FORGOT PASSWORD FORM */}
      {!resetSuccess && (
        <Grid direction="column" item md={6} xl={6} sm={12} xs={12} className={classes.forgotPasswordViewBlock}>
          <MuiBox className={classes.forgotPasswordTextBox}>
            <Typography className={classes.forgotPasswordHeader}>Forgot Your Password?</Typography>
            <MuiBox className={classes.forgotPasswordTextBorder} />
            <Typography className={classes.forgotPasswordSubHeader}>
              Type your email below and we will send you a link to reset your password.
            </Typography>
          </MuiBox>
          <form>
            {/* EMAIL INPUT */}
            <MuiBox className={classes.forgotPasswordEmailInput}>
              <InputField
                onChange={(e) => onInputChange(setEmail, e.target.value)}
                error={!!invalidMsg}
                helperText={emailValidationFn}
                label="Email"
                type="email"
                placeholder="Your Email"
                fullWidth
              />
            </MuiBox>
            {/* ERROR MESSAGE */}
            {invalidMsg && !updating && (
              <Box flexOverride="none" margin="0 0 5" height={24}>
                <Typography fontSize={16} color={color.RED}>
                  {invalidMsg}
                </Typography>
              </Box>
            )}
            {/* SUBMIT BUTTON */}
            <Button
              disabled={buttonDisabled}
              customSize="large"
              onClick={onSaveClick}
              fullWidth
              isLoading={updating}
              type="submit"
              className={classes.forgotPasswordSendEmailButton}
            >
              Send Email
            </Button>
          </form>
        </Grid>
      )}

      {resetSuccess && (
        <Grid direction="column" item md={6} xl={6} lg={6} sm={12} xs={12} className={classes.PasswordResetEmailBlock}>
          <Box flexOverride="none" margin="0 0 7">
            <Typography className={classes.passwordResetHeader}>An email has been sent.</Typography>
            <MuiBox className={classes.passwordResetDivider} />
            <Typography className={classes.passwordResetSubHeader}>
              Please check your inbox. An email has been sent containing a link to reset your password. You can now
              close this tab and follow the instructions found in the email.
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );
};

export default ForgotPasswordView;
